<script setup lang="ts">
import type { AccessChecklistItem, AccessRequirementKey } from '@peanuds/app-shell';
import { UiIconTextAction } from '@peanuds/ui';
import { computed } from 'vue';

import { getTotalBalanceOnboardingPresentation } from '../total-balance-onboarding.presentation';

interface Props {
  currentItem: AccessChecklistItem;
  horizontal?: boolean;
  loading?: boolean;
}

defineOptions({ name: 'TotalBalanceOnboardingContent' });

const { currentItem, horizontal = false, loading = false } = defineProps<Props>();

const emit = defineEmits<{
  action: [key: AccessRequirementKey];
}>();

const presentation = computed(() => getTotalBalanceOnboardingPresentation(currentItem));
const buttonLayout = computed(() => (horizontal ? 'inline' : 'stacked'));

const handleAction = () => {
  if (!currentItem.ctaVisible || currentItem.ctaDisabled) {
    return;
  }

  emit('action', currentItem.key);
};
</script>

<template>
  <div
    class="total-balance-onboarding-content"
    :class="{ 'total-balance-onboarding-content--horizontal': horizontal }"
    data-testid="total-balance-onboarding-content"
  >
    <UiIconTextAction
      :title="presentation.title"
      :subtitle="presentation.subtitle"
      :button="presentation.button"
      :icon-component="presentation.iconComponent"
      :icon-props="presentation.iconProps"
      :button-layout="buttonLayout"
      :loading="loading"
      @action="handleAction"
    />
  </div>
</template>

<style scoped lang="scss">
.total-balance-onboarding-content {
  width: 100%;

  &--horizontal {
    :deep(.ui-icon-text-action) {
      display: grid;
      grid-template-columns: auto minmax(0, 1fr);
      align-items: center;
    }

    :deep(.ui-icon-text-action__content) {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      gap: $gap-md;
      flex-shrink: none;
    }

    :deep(.ui-icon-text-action__button) {
      margin-top: 0;
    }
  }

  @media screen and (max-width: $screen_normal) {
    :deep(.ui-icon-text-action) {
      flex-direction: column;
      align-items: stretch;
    }

    :deep(.ui-icon-text-action__icon) {
      width: fit-content;
    }

    :deep(.ui-icon-text-action__content) {
      width: 100%;
    }

    :deep(.ui-icon-text-action__button) {
      width: 100%;
      justify-content: center;
    }
  }

  &--horizontal {
    @media screen and (max-width: $screen_normal) {
      :deep(.ui-icon-text-action) {
        display: flex;
        flex-direction: row;
      }

      :deep(.ui-icon-text-action__content) {
        display: grid;
        grid-template-columns: 6fr 2fr;
      }
    }
  }
}
</style>
